import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatDateAndTime } from '@racemap/utilities/formattings/times';
import { type FC, useEffect, useRef } from 'react';
import { useInfiniteScroll } from 'src/lib/customHooks/useInfiniteScroll';
import { getPlatformOfUserAgent, Platforms } from 'src/lib/userAgent';
import { EventLogo } from '../BasicComponents/EventLogo';
import { HighlightedText } from '../BasicComponents/HighlightedText';
import { KeyCap, type KeyHint, KeyHintsFooter } from '../BasicComponents/Hotkeys';
import { IconChevronRight, IconClockRotateLeft, IconUser } from '../BasicComponents/Icons';
import { LoadingIndicator } from '../BasicComponents/LoadingIndicator';
import { PaymentStateBadge, RunningBadge } from '../EventInfoBadges';
import { SearchTabs } from './SearchTabs';
import type {
  ActionSearchItem,
  EventSearchItem,
  PageSearchItem,
  SearchCategory,
  SearchGroup,
  SearchItem,
  SearchTab,
  UserSearchItem,
} from './useSearchResults';

const isMac = getPlatformOfUserAgent(navigator.userAgent) === Platforms.MAC;

const ResultHints: Array<KeyHint> = [
  { keys: ['↑', '↓'], label: 'move' },
  { keys: ['⏎'], label: 'open' },
  { keys: [isMac ? '⌘' : 'Ctrl', '⏎'], label: 'new tab' },
  { keys: ['esc'], label: 'close' },
];
const EmptyHints: Array<KeyHint> = [{ keys: ['esc'], label: 'close' }];

/** Ties a row to the input's aria-activedescendant. */
export const optionId = (listboxId: string, index: number) => `${listboxId}-option-${index}`;

/**
 * Ctrl/Cmd is the browser's own new-tab modifier, on a click and on a focused link alike.
 * Shift means a new window there, but is accepted too: it costs nothing and is easy to reach for.
 */
export const opensInNewTab = (event: {
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}): boolean => event.ctrlKey || event.metaKey || event.shiftKey;

interface Props {
  groups: Array<SearchGroup>;
  tabs: Array<SearchTab>;
  activeCategory: SearchCategory;
  onSelectCategory: (category: SearchCategory) => void;
  query: string;
  activeIndex: number;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  hasNextPage: boolean;
  onLoadNextPage: () => void;
  onSelect: (item: SearchItem, inNewTab: boolean) => void;
  onHover: (index: number) => void;
  listboxId: string;
  fullWidth?: boolean;
}

export const SearchPanel: FC<Props> = ({
  groups,
  tabs,
  activeCategory,
  onSelectCategory,
  query,
  activeIndex,
  isLoading,
  hasError,
  onRetry,
  hasNextPage,
  onLoadNextPage,
  onSelect,
  onHover,
  listboxId,
  fullWidth = false,
}) => {
  const sentinelRef = useInfiniteScroll({
    onLoadMore: onLoadNextPage,
    hasMore: hasNextPage,
    isLoading,
  });
  const isEmpty = groups.every((group) => group.items.length === 0);
  // Runs across all groups: the index space the keyboard cursor moves in.
  let renderIndex = -1;

  return (
    <Container fullWidth={fullWidth}>
      {/* Outside the listbox, which may only hold options and groups, and above the scroller so
          the bar cannot scroll away. */}
      <SearchTabs
        tabs={tabs}
        activeCategory={activeCategory}
        onSelect={onSelectCategory}
        fullWidth={fullWidth}
      />
      {/* biome-ignore lint/a11y/useSemanticElements: a <select> cannot hold rich rows, grouped
          categories or a footer, and this listbox is driven by a separate combobox input */}
      <Scroller role="listbox" id={listboxId} aria-label="Search results">
        {hasError && (
          <ErrorState>
            <div>Search failed.</div>
            <LoadingIndicator isLoading={false} borderless onReload={onRetry} />
          </ErrorState>
        )}
        {!hasError && isEmpty && isLoading && <LoadingIndicator borderless text="Searching..." />}
        {!hasError && isEmpty && !isLoading && <NoResults>No results found.</NoResults>}

        {groups.map((group, groupIndex) => (
          // biome-ignore lint/a11y/useSemanticElements: a <fieldset> is not valid inside a listbox
          <div key={group.key} role="group" aria-label={group.title}>
            <GroupTitle aria-hidden withSeparator={groupIndex > 0}>
              {group.title}
            </GroupTitle>
            {group.items.map((item) => {
              renderIndex += 1;
              const index = renderIndex;

              return (
                <Row
                  key={item.key}
                  id={optionId(listboxId, index)}
                  item={item}
                  query={query}
                  isActive={index === activeIndex}
                  onSelect={(inNewTab) => onSelect(item, inNewTab)}
                  onHover={() => onHover(index)}
                />
              );
            })}
          </div>
        ))}

        {!isEmpty && isLoading && <LoadingIndicator borderless />}
        <div ref={sentinelRef} />
      </Scroller>

      <KeyHintsFooter hints={isEmpty ? EmptyHints : ResultHints} />
    </Container>
  );
};

interface RowProps {
  id: string;
  item: SearchItem;
  query: string;
  isActive: boolean;
  onSelect: (inNewTab: boolean) => void;
  onHover: () => void;
}

const Row: FC<RowProps> = ({ id, item, query, isActive, onSelect, onHover }) => {
  const rowRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isActive) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isActive]);

  return (
    <RowButton
      ref={rowRef}
      type="button"
      id={id}
      // biome-ignore lint/a11y/useSemanticElements: an <option> renders as plain text, not the
      // logo, badges and marked-up name a result row needs
      role="option"
      aria-selected={isActive}
      isActive={isActive}
      onMouseMove={onHover}
      onClick={(event) => onSelect(opensInNewTab(event))}
    >
      {item.kind === 'event' && <EventRowContent item={item} query={query} />}
      {item.kind === 'page' && <PageRowContent item={item} query={query} />}
      {item.kind === 'user' && <UserRowContent item={item} query={query} />}
      {item.kind === 'action' && <ActionRowContent item={item} query={query} />}
      {item.kind === 'recent' && (
        <>
          <Glyph>
            <IconClockRotateLeft />
          </Glyph>
          <Details>
            <Name>{item.query}</Name>
          </Details>
        </>
      )}
      {isActive && (
        <Affordance>
          <KeyCap>⏎</KeyCap>
        </Affordance>
      )}
    </RowButton>
  );
};

const EventRowContent: FC<{ item: EventSearchItem; query: string }> = ({ item, query }) => {
  const { event } = item;

  return (
    <>
      <EventLogo event={event} size={42} rounded={4} />
      <Details>
        <Name>
          <HighlightedText text={event.name || event.id} query={query} />
        </Name>
        <SubLine>
          {/* The template time, not currentEventTimes: the back office wants the original. */}
          {[formatDateAndTime(event.startTime, true), event.location]
            .filter((part) => part != null && part !== '')
            .map((part, index) => (
              <span key={part}>
                {index > 0 && ' · '}
                <HighlightedText text={part} query={query} />
              </span>
            ))}
        </SubLine>
        <Badges>
          <RunningBadge event={event} />
          <PaymentStateBadge event={event} />
        </Badges>
      </Details>
    </>
  );
};

const PageRowContent: FC<{ item: PageSearchItem; query: string }> = ({ item, query }) => (
  <>
    <Glyph>
      <IconChevronRight />
    </Glyph>
    <Details>
      <CompactName>
        <HighlightedText text={item.page.title} query={query} />
      </CompactName>
      {item.page.section != null && <SubLine>{item.page.section}</SubLine>}
    </Details>
  </>
);

const UserRowContent: FC<{ item: UserSearchItem; query: string }> = ({ item, query }) => (
  <>
    <Glyph>
      <IconUser />
    </Glyph>
    <Details>
      <CompactName>
        <HighlightedText text={item.user.name || item.user.email} query={query} />
      </CompactName>
      {/* Names repeat; the address is what tells two accounts apart, so it is searched too. */}
      <SubLine>
        <HighlightedText text={item.user.email} query={query} />
      </SubLine>
    </Details>
  </>
);

const ActionRowContent: FC<{ item: ActionSearchItem; query: string }> = ({ item, query }) => (
  <>
    <Glyph>{item.action.icon}</Glyph>
    <Details>
      <CompactName>
        <HighlightedText text={item.action.title} query={query} />
      </CompactName>
    </Details>
  </>
);

// fullWidth means the panel sits inside the mobile sheet, which already provides the frame.
const Container = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '600px')};
  max-width: 100vw;
  background: #fff;
  overflow: hidden;
  ${({ fullWidth }) =>
    fullWidth
      ? ''
      : `border: 1px solid ${RacemapColors.CloudBlue};
         border-radius: 6px;
         box-shadow: 0 14px 40px rgba(16, 32, 48, 0.24);`}
`;

// Unpadded, so rows run edge to edge and the active row's accent bar sits on the panel border.
const Scroller = styled.div`
  overflow-y: auto;
  min-height: 0;
  max-height: min(60vh, 420px);
`;

const GroupTitle = styled.div<{ withSeparator: boolean }>`
  padding: 11px 14px 5px;
  border-top: ${({ withSeparator }) =>
    withSeparator ? `1px solid ${RacemapColors.LightGray}` : 'none'};
  color: ${RacemapColors.DarkGray};
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.7px;
  text-transform: uppercase;
`;

const RowButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  text-align: left;
  cursor: pointer;
  background: ${({ isActive }) => (isActive ? RacemapColors.LightBlue : 'transparent')};
  box-shadow: ${({ isActive }) => (isActive ? `inset 3px 0 0 ${RacemapColors.PaleBlue}` : 'none')};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

const Name = styled.div`
  font-size: 14.5px;
  font-weight: 600;
  color: ${RacemapColors.DarkBlue};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The short rows carry no logo, so their titles sit a step in from the event titles, per the design.
const CompactName = styled(Name)`
  font-size: 13.5px;
  font-weight: 500;
  color: ${RacemapColors.DarkDarkGray};
`;

const SubLine = styled.div`
  color: ${RacemapColors.DarkGray};
  font-size: 12.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The shared badges are sized for page content. Scoped here so their other uses are untouched.
const Badges = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 4px;

  /* The badges are shared components, so their Tags take no styles prop from here. Matched
     structurally rather than by .ant-tag, which shifts between antd majors. */
  > span {
    /* antd spaces tags with a trailing margin; the flex gap above replaces it. */
    margin-inline-end: 0;
    padding: 0 5px;
    border-radius: 3px;
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.4px;
    line-height: 16px;
  }
`;

// The design's 22px column for every row without a logo: pages, users, actions and recents.
const Glyph = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  color: ${RacemapColors.DarkGray};
`;

const Affordance = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  align-self: center;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  padding: 11px 14px;
  color: ${RacemapColors.DarkDarkGray};
`;

const NoResults = styled.div`
  padding: 11px 14px;
  color: ${RacemapColors.DarkDarkGray};
`;
